export default class PerlinNoise3D {
    private perlin_octaves: number;
    private perlin_amp_falloff: number;
    private perlin_size: number;
    private perlin_min: number;
    private perlin_max: number;
    private perlin_scale: number;
    private perlin: Array<number>;

    constructor(min = 0, max = 1, scale = 1, octaves = 4, amp_falloff = 0.5) {
        this.perlin_octaves = octaves;
        this.perlin_amp_falloff = amp_falloff;
        this.perlin_size = 4095;
        this.perlin_min = min;
        this.perlin_max = max;
        this.perlin_scale = scale;
        this.perlin = [];
    }

    get(x: number, y = 0, z = 0) {
        const PERLIN_YWRAPB = 4;
        const PERLIN_YWRAP = 1 << PERLIN_YWRAPB;
        const PERLIN_ZWRAPB = 8;
        const PERLIN_ZWRAP = 1 << PERLIN_ZWRAPB;
        const PERLIN_SIZE = this.perlin_size;
        const scaled_cosine = (i:number) => 0.5 * (1.0 - Math.cos(i * Math.PI));
        if (this.perlin.length === 0) {
            this.perlin = new Array(PERLIN_SIZE + 1);
            for (let i = 0; i < PERLIN_SIZE + 1; i++) {
                this.perlin[i] = Math.random();
            }
        }
        if (x < 0) {
            x = -x;
        }
        if (y < 0) {
            y = -y;
        }
        if (z < 0) {
            z = -z;
        }
        x *= this.perlin_scale;
        y *= this.perlin_scale;
        z *= this.perlin_scale;
        let xi = Math.floor(x),
            yi = Math.floor(y),
            zi = Math.floor(z);
        let xf = x - xi;
        let yf = y - yi;
        let zf = z - zi;
        let rxf, ryf;
        let r = 0;
        let ampl = 0.5;
        let n1, n2, n3;
        for (let o = 0; o < this.perlin_octaves; o++) {
            let of = xi + (yi << PERLIN_YWRAPB) + (zi << PERLIN_ZWRAPB);
            rxf = scaled_cosine(xf);
            ryf = scaled_cosine(yf);
            n1 = this.perlin[of & PERLIN_SIZE];
            n1 += rxf * (this.perlin[(of + 1) & PERLIN_SIZE] - n1);
            n2 = this.perlin[(of + PERLIN_YWRAP) & PERLIN_SIZE];
            n2 += rxf * (this.perlin[(of + PERLIN_YWRAP + 1) & PERLIN_SIZE] - n2);
            n1 += ryf * (n2 - n1);
            of += PERLIN_ZWRAP;
            n2 = this.perlin[of & PERLIN_SIZE];
            n2 += rxf * (this.perlin[(of + 1) & PERLIN_SIZE] - n2);
            n3 = this.perlin[(of + PERLIN_YWRAP) & PERLIN_SIZE];
            n3 += rxf * (this.perlin[(of + PERLIN_YWRAP + 1) & PERLIN_SIZE] - n3);
            n2 += ryf * (n3 - n2);
            n1 += scaled_cosine(zf) * (n2 - n1);
            r += n1 * ampl;
            ampl *= this.perlin_amp_falloff;
            xi <<= 1;
            xf *= 2;
            yi <<= 1;
            yf *= 2;
            zi <<= 1;
            zf *= 2;
            if (xf >= 1.0) {
                xi++;
                xf--;
            }
            if (yf >= 1.0) {
                yi++;
                yf--;
            }
            if (zf >= 1.0) {
                zi++;
                zf--;
            }
        }
        return r * (this.perlin_max - this.perlin_min) + this.perlin_min;
    }

    noiseBoundaries(min = 0, max = 1) {
        this.perlin_min = min;
        this.perlin_max = max;
    }

    noiseScale(scale = 1) {
        if (scale > 0) {
            this.perlin_scale = scale;
        }
    }

    noiseDetail(octaves = 4, amp_falloff = 0.5) {
        if (octaves > 0) {
            this.perlin_octaves = octaves;
        }
        if (amp_falloff > 0) {
            this.perlin_amp_falloff = amp_falloff;
        }
    };

    noiseSeed(seed: number | null) {
        const lcg = (() => {
            const m = 4294967296;
            const a = 1664525;
            const c = 1013904223;
            let seed: number, z: number;
            return {
                setSeed(val: number | null) {
                    z = seed = (val == null ? Math.random() * m : val) >>> 0;
                },
                getSeed() {
                    return seed;
                },
                rand() {
                    z = (a * z + c) % m;
                    return z / m;
                }
            };
        })();
        lcg.setSeed(seed);
        this.perlin = new Array(this.perlin_size + 1);
        for (let i = 0; i < this.perlin_size + 1; i++) {
            this.perlin[i] = lcg.rand();
        }
    };
}